import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import CfriShell from "./CfriShell";
import { LandscapeChart, AREA_KINDS } from "./CfriFigures";
import { QuadrantChart } from "./CfriFigures";
import { MapFrame, ProvenanceTag, fmt } from "./CfriUI";
import { SectionCard } from "../funder/FunderUI";

export default function CfriOverview() {
    return (
        <CfriShell active="overview">
            {(content) => <Overview content={content} />}
        </CfriShell>
    );
}

function Overview({ content }) {
    const navigate = useNavigate();
    const { overview, proposals, figures, readme } = content;
    const open = (id) => navigate(`/cfri/proposal/${id}`);

    const legendRow = (kind, text) => (
        <div style={{ marginBottom: 12 }}>
            <ProvenanceTag kind={kind} />
            <div style={{ fontSize: 12.5, color: "#eef3f0", lineHeight: 1.45, marginTop: 5 }}>{text}</div>
        </div>
    );

    return (
        <div className="funder-section-stack">
            <div className="funder-page-header">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
                    <div style={{ flex: "1 1 380px", minWidth: 0 }}>
                        <div className="funder-overline">CFRI · {proposals.length} proposals</div>
                        <h1 className="funder-h1">{overview.title}</h1>
                        {overview.intro.map((para, i) => (
                            <p key={i} className="funder-subtitle" style={{ margin: "0 0 10px" }}>{para}</p>
                        ))}
                        <button className="funder-btn" style={{ marginTop: 8, background: "#5ecfa0", color: "#10271f" }} onClick={() => open(proposals[0].id)}>
                            Start with {proposals[0].shortName} <ArrowRight size={15} />
                        </button>
                    </div>

                    <div className="cfri-header-panel">
                        <strong style={{ color: "#fff", fontSize: 14, display: "block", marginBottom: 12 }}>How to read it</strong>
                        {legendRow("computed", "Counted from the published literature. The same inputs give the same result.")}
                        {legendRow("engine", "Drafted by the 42Degrees assessment engine from the papers it cites: a prepared reviewer's notes, not a score.")}
                        {readme?.handling && (
                            <div style={{ fontSize: 11.5, color: "#8fb3a6", lineHeight: 1.45, borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 10 }}>
                                {readme.handling}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <SectionCard kicker="The four at a glance" title="Where each proposal sits today" subtitle="Open a proposal for its closest published work, assessment, probe questions and suggested reviewers.">
                <div className="cfri-stack-wrap">
                    {/* cfri-stack: in a narrow card each row becomes a card, labeled by data-label */}
                    <table className="funder-table cfri-table cfri-stack">
                        <thead>
                            <tr>
                                <th>Proposal</th>
                                <th className="cfri-num">Papers close to it</th>
                                <th className="cfri-num">Published near it since submission</th>
                                <th>Corner</th>
                                <th className="cfri-num">Reviewers suggested</th>
                            </tr>
                        </thead>
                        <tbody>
                            {proposals.map((p) => (
                                <tr key={p.id} className="funder-row-clickable" onClick={() => open(p.id)}>
                                    <td className="funder-title-cell">
                                        <a className="cfri-row-link" href={`/cfri/proposal/${p.id}`} onClick={(e) => { e.preventDefault(); open(p.id); }}>
                                            {p.applicant}
                                        </a>
                                        <div className="cfri-muted" style={{ fontSize: 12.5, marginTop: 2 }}>{p.title}</div>
                                    </td>
                                    <td className="cfri-num" data-label="Papers close to it"><span>{fmt(p.glance.papersClose)}</span></td>
                                    <td className="cfri-num" data-label="Published near it since submission"><span>{fmt(p.glance.sinceSubmission)}</span></td>
                                    <td data-label="Corner"><span>{p.glance.corner}</span></td>
                                    <td className="cfri-num" data-label="Reviewers suggested">
                                        <div>
                                            {p.glance.reviewers.total}
                                            <div className="cfri-muted" style={{ fontSize: 11.5 }}>{p.glance.reviewers.withEmail} with an email</div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </SectionCard>

            <SectionCard
                kicker="The cystic fibrosis literature"
                title={`${figures.landscape.areas.length} research areas, with the proposals placed on them`}
                subtitle={overview.figures.landscape?.text}
            >
                <LandscapeChart data={figures.landscape} onSelectProposal={open} />
                <details className="cfri-details">
                    <summary>The research areas each proposal is joined to</summary>
                    <div className="cfri-joined">
                        {figures.landscape.proposals.map((p) => (
                            <div key={p.id}>
                                <div className="cfri-joined-name">{p.name}</div>
                                <ul>
                                    {p.near.map((i) => {
                                        const [, , n, kind, title] = figures.landscape.areas[i];
                                        return (
                                            <li key={i}>
                                                {title} <span className="cfri-muted">· {(AREA_KINDS.find((k) => k.key === kind) || {}).label} · {fmt(n)} papers</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                </details>
            </SectionCard>

            <SectionCard
                kicker="Crowded and growing"
                title="How crowded each proposal's corner of CF research is, and how fast it is growing"
                subtitle={overview.figures.quadrant?.text}
            >
                <QuadrantChart data={figures.quadrant} onSelectProposal={open} />
            </SectionCard>

            <SectionCard kicker="Advanced" title="The interactive map" subtitle={overview.leadMapNote}>
                <MapFrame id="lead" title="Interactive map of the CFRI proposals on the CF literature" />
            </SectionCard>

            <p className="cfri-footnote">
                {overview.disclaimer} {overview.built}.{" "}
                <span className="funder-link" onClick={() => navigate("/cfri/method")}>How this was computed</span>
            </p>
        </div>
    );
}
